import React, { memo, useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { DatePicker } from '@atlaskit/datetime-picker';

import type { FieldBaseProps } from '../common/types';

import type { JiraDatePickerField_Platform$key } from './__generated__/JiraDatePickerField_Platform.graphql';
import { BaseDateField, type BaseDateFieldProps } from './BaseDateField';

const messages = defineMessages({
	datePickerLabel: {
		id: 'jira.jira.jira-platform-fields.jira-date-picker-field.date-picker-label',
		defaultMessage: 'Date',
		description: 'Label for the date picker field',
	},
});

export type Props = FieldBaseProps & {
	field: JiraDatePickerField_Platform$key;
	onChange?: (newValue: string | null) => void;
};

export const JiraDatePickerField: React.MemoExoticComponent<
	({ field, fieldValue, isFieldEditable, onChange }: Props) => JSX.Element | null
> = memo(({ field, fieldValue, isFieldEditable = true, onChange }: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment<JiraDatePickerField_Platform$key>(
		graphql`
			fragment JiraDatePickerField_Platform on JiraDatePickerField {
				name
				fieldConfig {
					isEditable
					isRequired
				}
				date
			}
		`,
		field,
	);

	const { isRequired } = data?.fieldConfig || {};
	const isEditable = data?.fieldConfig?.isEditable && isFieldEditable;

	const defaultValue = data?.date;
	const currentValue = typeof fieldValue === 'string' ? fieldValue : null;

	const renderPicker = useCallback(
		(renderPickerProps: Parameters<BaseDateFieldProps['renderPicker']>[0]) => (
			<DatePicker
				{...renderPickerProps}
				label={formatMessage(messages.datePickerLabel)}
				shouldShowCalendarButton
			/>
		),
		[formatMessage],
	);

	return (
		<BaseDateField
			fieldName={data?.name}
			labelText={formatMessage(messages.datePickerLabel)}
			isEditable={!!isEditable}
			isRequired={!!isRequired}
			currentValue={currentValue}
			defaultValue={defaultValue}
			includeTime={false}
			onChange={onChange}
			renderPicker={renderPicker}
		/>
	);
});
